import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { toFacilityDate } from "@alm/domain";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { Printer } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { optionalDay, searchPatch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { CarePassLogTable } from "@/routes/_authenticated/_care/care-pass/-components/care-pass-log-table";
import { CarePassWeek } from "@/routes/_authenticated/_care/care-pass/-components/care-pass-week";
import { ProfileSection } from "./-components/chart/profile-section";

type PassSearch = { from?: string; to?: string };

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/care-pass")({
  validateSearch: (search: Record<string, unknown>): PassSearch => ({
    from: optionalDay(search.from),
    to: optionalDay(search.to),
  }),
  component: ResidentCarePassTab,
});

/** The resident's care pass: their week, and every mark made over the days chosen, printable as a
 * MAR-style grid. */
function ResidentCarePassTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const timezone = resident.facilityTimezone ?? "UTC";
  const today = toFacilityDate(new Date(), timezone);
  const params = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const monthStart = `${today.slice(0, 7)}-01`;
  const from = params.from ?? monthStart;
  const to = params.to ?? today;
  const setFrom = (next: string) => patch({ from: next === monthStart ? undefined : next });
  const setTo = (next: string) => patch({ to: next === today ? undefined : next });
  const canManage = useCan("carepass:manage", resident.facilityId);
  const valid = from <= to;
  const history = useQuery({
    ...orpc.care.carePass.forResident.queryOptions({ input: { residentId, from, to } }),
    enabled: valid,
  });

  return (
    <div className="space-y-4">
      <ProfileSection
        title="Care pass"
        description="Laundry, room cleaning, bathing and supervision, as recorded shift by shift."
        action={
          <>
            <DatePicker value={from} onChange={setFrom} aria-label="From" className="w-36" />
            <DatePicker value={to} onChange={setTo} aria-label="To" className="w-36" />
            <Button variant="outline" asChild>
              <Link
                to="/care-pass/print"
                search={{ residentId, from, to }}
                target="_blank"
                rel="noopener"
              >
                <Printer className="size-4" />
                Print
              </Link>
            </Button>
          </>
        }
      >
        {!valid ? (
          <p role="alert" className="text-crit text-sm">
            The first day is after the last. Pick a span that runs forwards.
          </p>
        ) : history.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : history.isError ? (
          <ReadFailed
            message="Couldn't load this resident's care pass."
            error={history.error}
            retrying={history.isFetching}
            onRetry={() => void history.refetch()}
          />
        ) : (
          <div className="space-y-6">
            <CarePassWeek
              shifts={history.data.shifts}
              slots={history.data.slots}
              scheduleLink={canManage}
            />
            <CarePassLogTable logs={history.data.logs} timezone={timezone} />
          </div>
        )}
      </ProfileSection>
    </div>
  );
}
